import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type FocusEvent,
  type ReactNode,
} from 'react'

import {ComposerTelemetryTracker, type ComposerTelemetryConfig} from './composer-telemetry'
import type {ComposerContextType, ComposerSurface} from './constants'

const ComposerTelemetryContext = createContext<ComposerTelemetryTracker | null>(null)

export interface ComposerTelemetryProviderProps {
  surface: ComposerSurface
  contextType?: ComposerContextType
  directTurnSurface?: string
  /** Extra fields for the `shown` event. */
  hasInitialPrompt?: boolean
  isNewConversation?: boolean
  /** Test seam: inject a tracker instead of creating one. */
  tracker?: ComposerTelemetryTracker
  children: ReactNode
}

/**
 * Owns one {@link ComposerTelemetryTracker} for the lifetime of a mounted
 * composer. Descendant controls report through {@link useComposerTelemetry};
 * without a provider those calls are no-ops, so shared controls stay usable
 * outside the composer.
 */
export function ComposerTelemetryProvider({
  surface,
  contextType,
  directTurnSurface,
  hasInitialPrompt,
  isNewConversation,
  tracker: injectedTracker,
  children,
}: ComposerTelemetryProviderProps) {
  const [tracker] = useState(
    () => injectedTracker ?? new ComposerTelemetryTracker({surface, contextType, directTurnSurface}),
  )

  const config: ComposerTelemetryConfig = {surface, contextType, directTurnSurface}
  useEffect(() => {
    tracker.updateConfig(config)
    // Fields are primitives; listing them avoids re-running for a new object identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tracker, surface, contextType, directTurnSurface])

  const shownFieldsRef = useRef({hasInitialPrompt, isNewConversation})
  useEffect(() => {
    tracker.shown(shownFieldsRef.current)
  }, [tracker])

  useEffect(() => {
    const onPageHide = () => {
      tracker.abandon('pagehide')
      tracker.flushPending()
    }
    // The page may be discarded while hidden without a pagehide, so queued events go out now.
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') tracker.flushPending()
    }
    window.addEventListener('pagehide', onPageHide)
    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => {
      window.removeEventListener('pagehide', onPageHide)
      document.removeEventListener('visibilitychange', onVisibilityChange)
      // Strict Mode's simulated unmount happens before any interaction, so this cannot fire spuriously.
      tracker.abandon('unmount')
    }
  }, [tracker])

  return <ComposerTelemetryContext.Provider value={tracker}>{children}</ComposerTelemetryContext.Provider>
}

export function useComposerTelemetry(): ComposerTelemetryTracker | null {
  return useContext(ComposerTelemetryContext)
}

/**
 * Returns an `onFocusCapture` handler for the composer root that reports the
 * first focus. Focus that arrives before any pointer or keyboard input since
 * mount is reported as autofocus.
 */
export function useComposerFocusTelemetry(): (event: FocusEvent<HTMLElement>) => void {
  const telemetry = useComposerTelemetry()
  const userInputSeenRef = useRef(false)

  useEffect(() => {
    if (!telemetry) return
    const markUserInput = () => {
      userInputSeenRef.current = true
    }
    window.addEventListener('pointerdown', markUserInput, {capture: true, once: true})
    window.addEventListener('keydown', markUserInput, {capture: true, once: true})
    return () => {
      window.removeEventListener('pointerdown', markUserInput, {capture: true})
      window.removeEventListener('keydown', markUserInput, {capture: true})
    }
  }, [telemetry])

  return useCallback(() => telemetry?.focused(!userInputSeenRef.current), [telemetry])
}
